import {z} from '@hono/zod-openapi';

import {ASSISTANT_TEMPLATE_SOURCE_MAX_CHARACTERS} from '../constants/ASSISTANT_TEMPLATE_SOURCE_MAX_CHARACTERS';
import {ASSISTANT_VARIABLE_CATALOG_MAX_ITEMS} from '../constants/ASSISTANT_VARIABLE_CATALOG_MAX_ITEMS';
import {ASSISTANT_VARIABLE_PATH_MAX_CHARACTERS} from '../constants/ASSISTANT_VARIABLE_PATH_MAX_CHARACTERS';
import {ASSISTANT_VARIABLE_SAMPLE_MAX_CHARACTERS} from '../constants/ASSISTANT_VARIABLE_SAMPLE_MAX_CHARACTERS';
import {PendingTemplateEditProposalSchema} from './PendingTemplateEditProposalSchema';

/**
 * Current Document Template Editor state supplied as untrusted model input.
 * The source is the editor baseline. When pendingProposal is present, its source is the current
 * proposed working revision. The variable catalog carries no authority outside prompt construction.
 */
export const assistantTemplateEditorContextSchema = z
  .discriminatedUnion('templateKind', [
    z.object({
      templateId: z.string().min(1),
      documentCode: z.string().min(1),
      templateTitle: z.string().min(1).max(500),
      templateKind: z.literal('document').optional().openapi({
        description: "Kind of template being edited. Absent means 'document' (legacy clients).",
      }),
      locale: z.string().optional(),
      source: z.string().max(ASSISTANT_TEMPLATE_SOURCE_MAX_CHARACTERS),
      pendingProposal: PendingTemplateEditProposalSchema.optional(),
      sourceTruncated: z.boolean().optional().openapi({
        description:
          'When true, source is an incomplete prefix and template editing tools must be withheld.',
      }),
      readOnly: z.boolean().optional().openapi({
        description: 'Template is a read-only system template; editing tools must be withheld.',
      }),
      logoConfigured: z.boolean().optional().openapi({
        description:
          'Whether an effective tenant or branch document logo is configured; informs template.logoUrl advice.',
      }),
      variableCatalog: z
        .array(
          z.object({
            path: z.string().min(1).max(ASSISTANT_VARIABLE_PATH_MAX_CHARACTERS),
            sample: z.string().max(ASSISTANT_VARIABLE_SAMPLE_MAX_CHARACTERS).optional(),
          })
        )
        .max(ASSISTANT_VARIABLE_CATALOG_MAX_ITEMS),
    }),
    z.object({
      templateId: z.string().min(1),
      documentCode: z.string().min(1),
      templateTitle: z.string().min(1).max(500),
      templateKind: z.literal('email'),
      context: z.enum([
        'CUSTOMER',
        'SERVICE_ORDER',
        'SERVICE_CASE',
        'BUSINESS_CASE',
        'RENTAL_ORDER',
        'VEHICLE',
        'INTEREST',
        'WORKSHOP_INTEREST',
      ]),
      channel: z.literal('email'),
      locale: z.string().optional(),
      source: z.string().max(ASSISTANT_TEMPLATE_SOURCE_MAX_CHARACTERS),
      pendingProposal: PendingTemplateEditProposalSchema.optional(),
      sourceTruncated: z.boolean().optional().openapi({
        description:
          'When true, source is an incomplete prefix and template editing tools must be withheld.',
      }),
      variableCatalog: z
        .array(
          z.object({
            path: z.string().min(1).max(ASSISTANT_VARIABLE_PATH_MAX_CHARACTERS),
            sample: z.string().max(ASSISTANT_VARIABLE_SAMPLE_MAX_CHARACTERS).optional(),
          })
        )
        .max(ASSISTANT_VARIABLE_CATALOG_MAX_ITEMS),
    }),
  ])
  .openapi('AssistantTemplateEditorContext');

export type AssistantTemplateEditorContext = z.infer<typeof assistantTemplateEditorContextSchema>;
